import { AlertTriangle, Check, CircleDollarSign, LoaderCircle, Upload } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import {
  createPayment,
  submitClientAllocationInstructions,
  uploadPaymentDocument,
} from "../../lib/arRecoveryPaymentsApi";

export function ARRecoveryClientPaymentReporting({
  defaultClientId = "",
  defaultCustomerId = "",
  defaultOrganization = "",
  defaultAccountId = "",
  defaultAccountNumber = "",
  defaultCustomerName = "",
  defaultCustomerNumber = "",
  lockAccountContext = false,
  readOnly = false,
  onSubmitted,
}: {
  defaultClientId?: string;
  defaultCustomerId?: string;
  defaultOrganization?: string;
  defaultAccountId?: string;
  defaultAccountNumber?: string;
  defaultCustomerName?: string;
  defaultCustomerNumber?: string;
  lockAccountContext?: boolean;
  readOnly?: boolean;
  onSubmitted?: () => void | Promise<void>;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [supportFile, setSupportFile] = useState<File | null>(null);
  const [invoiceInstructions, setInvoiceInstructions] = useState([
    { invoiceId: "", invoiceNumber: "", operationalBalance: "", allocatedAmount: "" },
  ]);
  const [form, setForm] = useState({
    clientId: defaultClientId,
    customerId: defaultCustomerId,
    customerName: defaultCustomerName,
    customerNumber: defaultCustomerNumber,
    accountId: defaultAccountId,
    accountNumber: defaultAccountNumber,
    invoiceNumber: "",
    paymentDate: new Date().toISOString().slice(0, 10),
    amount: "",
    currency: "USD",
    paymentMethod: "ACH",
    reference: "",
    note: "",
    organization: defaultOrganization,
  });

  useEffect(() => {
    setForm((current) => ({
      ...current,
      clientId: current.clientId || defaultClientId,
      customerId: current.customerId || defaultCustomerId,
      organization: current.organization || defaultOrganization,
      accountId: current.accountId || defaultAccountId,
      accountNumber: current.accountNumber || defaultAccountNumber,
      customerName: current.customerName || defaultCustomerName,
      customerNumber: current.customerNumber || defaultCustomerNumber,
    }));
  }, [defaultAccountId, defaultAccountNumber, defaultClientId, defaultCustomerId, defaultCustomerName, defaultCustomerNumber, defaultOrganization]);

  function update(key: keyof typeof form, value: string) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (readOnly) {
      setError("Your Client Portal role is read only. Payment reports cannot be submitted.");
      return;
    }
    setSubmitting(true);
    setError("");
    setSuccess("");
    try {
      const reference = form.reference.trim();
      const result = await createPayment({
        clientId: form.clientId.trim(),
        customerId: form.customerId.trim() || undefined,
        customerName: form.customerName.trim(),
        customerNumber: form.customerNumber.trim(),
        accountId: form.accountId.trim() || undefined,
        accountNumber: form.accountNumber.trim(),
        paymentSource: "Client Portal",
        externalPaymentReference: reference,
        paymentDate: form.paymentDate,
        amountCents: Math.round(Number(form.amount) * 100),
        currency: form.currency,
        paymentMethod: form.paymentMethod,
        memo: [form.invoiceNumber.trim() ? `Reported invoice: ${form.invoiceNumber.trim()}` : "", form.note.trim()].filter(Boolean).join(" | "),
        reportedByOrganization: form.organization.trim(),
        idempotencyKey: `client-report:${form.clientId}:${reference}:${form.amount}:${form.paymentDate}`,
      });
      const selectedInvoices = invoiceInstructions.filter((invoice) => invoice.invoiceId.trim() && invoice.invoiceNumber.trim());
      if (selectedInvoices.length) {
        if (!form.accountId.trim()) throw new Error("Account ID is required when reporting invoice allocations.");
        await submitClientAllocationInstructions(result.payment.paymentId, {
          accountId: form.accountId.trim(),
          currency: form.currency,
          note: form.note.trim(),
          invoices: selectedInvoices.map((invoice) => ({
            invoiceId: invoice.invoiceId.trim(),
            invoiceNumber: invoice.invoiceNumber.trim(),
            originalAmountCents: Math.round(Number(invoice.operationalBalance) * 100),
            operationalBalanceCents: Math.round(Number(invoice.operationalBalance) * 100),
            allocatedAmountCents: Math.round(Number(invoice.allocatedAmount) * 100),
          })),
        });
      }
      if (supportFile) await uploadPaymentDocument(result.payment.paymentId, supportFile, "CLIENT_PAYMENT_SUPPORT");
      setSuccess(result.replay ? "This payment report was already submitted. No duplicate was created." : "Payment report submitted for review.");
      setSupportFile(null);
      setForm((current) => ({ ...current, amount: "", reference: "", note: "", invoiceNumber: "" }));
      setInvoiceInstructions([{ invoiceId: "", invoiceNumber: "", operationalBalance: "", allocatedAmount: "" }]);
      await onSubmitted?.();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Payment report could not be submitted.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <article className="rounded-[1.5rem] border border-[#BFEAFF] bg-white p-5 shadow-[0_20px_48px_rgba(15,23,42,.06)]">
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-[#BFEAFF] pb-4">
        <div>
          <p className="text-[11px] font-black uppercase tracking-[.16em] text-[#14B8A6]">Client Payment Reporting</p>
          <h2 className="mt-1 font-display text-2xl font-bold text-[#0A2540]">Report a customer payment</h2>
          <p className="mt-2 max-w-3xl text-sm font-semibold leading-7 text-[#475569]">Report a payment received directly by your organization. CollabRecover does not receive or hold these funds.</p>
        </div>
        <span className="inline-flex min-h-8 items-center rounded-full border border-[#99F6E4] bg-[#F0FDFA] px-3 text-[10px] font-black uppercase tracking-[.12em] text-[#0F766E]">Client scope only</span>
      </div>
      {error ? <div className="mt-4 flex gap-2 rounded-xl border border-[#FECACA] bg-[#FEF2F2] p-3 text-sm font-bold text-[#991B1B]" role="alert"><AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />{error}</div> : null}
      {success ? <div className="mt-4 flex gap-2 rounded-xl border border-[#99F6E4] bg-[#F0FDFA] p-3 text-sm font-bold text-[#115E59]" role="status"><Check className="mt-0.5 size-4 shrink-0" aria-hidden="true" />{success}</div> : null}
      <form className="mt-5 grid gap-3 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4" onSubmit={(event) => void submit(event)}>
        <div className="grid gap-3 sm:grid-cols-2">
          <ClientPaymentField label="Client ID" value={form.clientId} onChange={(value) => update("clientId", value)} required readOnly={Boolean(defaultClientId)} />
          <ClientPaymentField label="Organization" value={form.organization} onChange={(value) => update("organization", value)} required readOnly={Boolean(defaultOrganization)} />
          <ClientPaymentField label="Customer name" value={form.customerName} onChange={(value) => update("customerName", value)} required readOnly={lockAccountContext} />
          <ClientPaymentField label="Customer number" value={form.customerNumber} onChange={(value) => update("customerNumber", value)} readOnly={lockAccountContext} />
          <ClientPaymentField label="Account number" value={form.accountNumber} onChange={(value) => update("accountNumber", value)} readOnly={lockAccountContext} />
          <ClientPaymentField label="Account ID" value={form.accountId} onChange={(value) => update("accountId", value)} readOnly={lockAccountContext} />
          <ClientPaymentField label="Payment date" type="date" value={form.paymentDate} onChange={(value) => update("paymentDate", value)} required />
          <ClientPaymentField label="Amount" type="number" value={form.amount} onChange={(value) => update("amount", value)} required />
          <label className="grid gap-1.5 text-[10px] font-black uppercase tracking-[.12em] text-[#0A2540]">Payment method<select className="min-h-11 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-bold normal-case tracking-normal text-[#0A2540]" value={form.paymentMethod} onChange={(event) => update("paymentMethod", event.target.value)}><option>ACH</option><option>Check</option><option>Wire</option><option>Credit Card</option><option>Cash</option><option>Other</option></select></label>
          <ClientPaymentField label="Reference" value={form.reference} onChange={(value) => update("reference", value)} required />
        </div>
        <section className="grid gap-3 rounded-xl border border-[#BFEAFF] bg-white p-3" aria-label="Client invoice allocation instructions">
          <div className="flex flex-wrap items-start justify-between gap-3"><div><p className="text-[10px] font-black uppercase tracking-[.12em] text-[#14B8A6]">Invoice Selection</p><p className="mt-1 text-xs font-semibold leading-5 text-[#475569]">Optional client-reported instructions. Internal verification remains authoritative.</p></div><button className="min-h-9 rounded-full border border-[#BFEAFF] px-3 text-[10px] font-black uppercase tracking-[.1em] text-[#0A2540]" type="button" onClick={() => setInvoiceInstructions((current) => [...current, { invoiceId: "", invoiceNumber: "", operationalBalance: "", allocatedAmount: "" }])}>Add Invoice</button></div>
          {invoiceInstructions.map((invoice, index) => <div key={index} className="grid gap-2 rounded-xl border border-[#D7F1FF] bg-[#F4FBFF] p-3 sm:grid-cols-2"><ClientPaymentField label="Invoice ID" value={invoice.invoiceId} onChange={(value) => setInvoiceInstructions((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, invoiceId: value } : item))} /><ClientPaymentField label="Invoice number" value={invoice.invoiceNumber} onChange={(value) => setInvoiceInstructions((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, invoiceNumber: value } : item))} /><ClientPaymentField label="Operational balance" type="number" value={invoice.operationalBalance} onChange={(value) => setInvoiceInstructions((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, operationalBalance: value } : item))} /><ClientPaymentField label="Reported allocation" type="number" value={invoice.allocatedAmount} onChange={(value) => setInvoiceInstructions((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, allocatedAmount: value } : item))} />{invoiceInstructions.length > 1 ? <button className="min-h-9 rounded-full border border-[#FECACA] bg-white px-3 text-[10px] font-black uppercase tracking-[.1em] text-[#991B1B] sm:col-span-2" type="button" onClick={() => setInvoiceInstructions((current) => current.filter((_, itemIndex) => itemIndex !== index))}>Remove Invoice</button> : null}</div>)}
        </section>
        <label className="grid gap-1.5 text-[10px] font-black uppercase tracking-[.12em] text-[#0A2540]">Note<textarea className="min-h-24 resize-y rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-semibold normal-case tracking-normal text-[#0A2540]" value={form.note} onChange={(event) => update("note", event.target.value)} /></label>
        <label className="inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-xl border border-[#BFEAFF] bg-white px-3 text-xs font-black text-[#0A2540]"><Upload className="size-4 text-[#14B8A6]" aria-hidden="true" />{supportFile ? supportFile.name : "Attach supporting document"}<input className="sr-only" type="file" onChange={(event) => setSupportFile(event.target.files?.[0] || null)} /></label>
        <div className="rounded-xl border border-[#BFEAFF] bg-white p-3 text-xs font-semibold leading-5 text-[#475569]">Review customer, amount, date, method, and reference before submitting. Matching and approval are completed by authorized CollabRecover operators.</div>
        <button className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-[#14B8A6] px-4 text-xs font-black text-white transition hover:bg-[#0F9F91] disabled:opacity-50 focus-ring" type="submit" disabled={submitting || readOnly}>{submitting ? <LoaderCircle className="size-4 animate-spin" aria-hidden="true" /> : <CircleDollarSign className="size-4" aria-hidden="true" />}Submit Payment Report</button>
      </form>
    </article>
  );
}

function ClientPaymentField({ label, value, onChange, type = "text", required = false, readOnly = false }: { label: string; value: string; onChange: (value: string) => void; type?: string; required?: boolean; readOnly?: boolean }) {
  return <label className="grid gap-1.5 text-[10px] font-black uppercase tracking-[.12em] text-[#0A2540]">{label}<input className="min-h-11 min-w-0 rounded-xl border border-[#BFEAFF] bg-white px-3 text-sm font-bold normal-case tracking-normal text-[#0A2540] read-only:bg-[#EFF6FF]" type={type} min={type === "number" ? "0.01" : undefined} step={type === "number" ? "0.01" : undefined} required={required} readOnly={readOnly} value={value} onChange={(event) => onChange(event.target.value)} /></label>;
}
